// src/utils/deliveryValidator.js

const DISTRICT_SUGGESTIONS = {
  'san isidro': 'San Isidro',
  sanisidro: 'San Isidro',
  milaflores: 'Miraflores',
  miraflres: 'Miraflores',
  chorillos: 'Chorrillos',
  chorrilos: 'Chorrillos',
  'villa el salvador': 'Villa El Salvador',
  'villa salvador': 'Villa El Salvador',
  'san borja': 'San Borja',
  sanborja: 'San Borja',
  'santiago de surco': 'Santiago de Surco',
  surco: 'Santiago de Surco',
  'san juan de miraflores': 'San Juan de Miraflores',
  sjm: 'San Juan de Miraflores',
  'la molina': 'La Molina',
  lamolina: 'La Molina',
  'san martin de porres': 'San Martin de Porres',
  smp: 'San Martin de Porres',
  'los olivos': 'Los Olivos',
  losolivos: 'Los Olivos',
  'jesus maria': 'Jesus Maria',
  'magdalena del mar': 'Magdalena del Mar',
  magdalena: 'Magdalena del Mar',
  'pueblo libre': 'Pueblo Libre',
  'san miguel': 'San Miguel',
  sanmiguel: 'San Miguel',
  'el agustino': 'El Agustino',
  'la victoria': 'La Victoria',
  'san juan de lurigancho': 'San Juan de Lurigancho',
  sjl: 'San Juan de Lurigancho',
  ate: 'Ate',
  'santa anita': 'Santa Anita',
  callao: 'Callao',
  ventanilla: 'Ventanilla',
  'puente piedra': 'Puente Piedra',
  carabayllo: 'Carabayllo',
  comas: 'Comas',
  independencia: 'Independencia',
  rimac: 'Rimac',
  barranco: 'Barranco',
  surquillo: 'Surquillo',
  lince: 'Lince',
  brena: 'Brena',
  'cercado de lima': 'Cercado de Lima',
  lima: 'Cercado de Lima',
};

const MAX_BULK_ITEMS = 5;

function normalizeDistrict(value) {
  return value
    ?.toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function getBulkItems(row) {
  const items = [];

  for (let index = 1; index <= MAX_BULK_ITEMS; index += 1) {
    const sku = row[`SKU ${index}`]?.toString().trim() || '';
    const quantity = row[`Cantidad ${index}`]?.toString().trim() || '';

    if (sku) {
      items.push({ index, sku, quantity: quantity || '1' });
    }
  }

  if (items.length === 0) {
    const legacySku = row['SKU']?.toString().trim() || '';
    const legacyQuantity = row['Cantidad']?.toString().trim() || '1';
    const skus = legacySku.split(',').map(s => s.trim()).filter(Boolean);
    const quantities = legacyQuantity.includes(',')
      ? legacyQuantity.split(',').map(q => q.trim())
      : [];

    skus.forEach((sku, index) => {
      items.push({
        index: index + 1,
        sku,
        quantity: quantities[index] || legacyQuantity || '1',
      });
    });
  }

  return items.slice(0, MAX_BULK_ITEMS);
}

function validDistrictListFrom(validDistricts) {
  const namesFromDistrict = (district) => {
    if (!district) return [];
    if (typeof district === 'string') return [district];
    return [
      district.name,
      district.slug,
      ...(district.aliases || []),
    ].filter(Boolean);
  };

  if (Array.isArray(validDistricts)) {
    return validDistricts.flatMap(namesFromDistrict);
  }
  if (Array.isArray(validDistricts.districts)) {
    return validDistricts.districts.flatMap(namesFromDistrict);
  }
  if (Array.isArray(validDistricts.all_districts)) return validDistricts.all_districts;
  return Object.keys(validDistricts || {});
}

function parseCoordinate(value) {
  const raw = value?.toString().trim() || '';
  if (!raw) return { raw: '', number: null };

  const normalized = raw.replace(',', '.');
  const number = Number(normalized);
  if (!Number.isFinite(number)) {
    return { raw, number: null, invalid: true };
  }

  return { raw: normalized, number };
}

export function validateDelivery(row, rowNumber, validDistricts = {}) {
  const errors = [];
  const warnings = [];
  const suggestions = {};

  const name = row['Nombre y Apellido']?.toString().trim() || '';
  if (!name) {
    errors.push({ field: 'name', message: 'Nombre y Apellido es obligatorio', severity: 'error' });
  } else if (name.length < 3) {
    warnings.push({ field: 'name', message: 'Nombre parece muy corto', severity: 'warning' });
  }

  const phone = row['WhatsApp']?.toString().trim() || '';
  const cleanPhone = phone.replace(/\D/g, '');
  if (!phone) {
    errors.push({ field: 'phone', message: 'WhatsApp es obligatorio', severity: 'error' });
  } else if (cleanPhone.length !== 9) {
    errors.push({
      field: 'phone',
      message: `WhatsApp debe tener exactamente 9 digitos (tiene ${cleanPhone.length})`,
      severity: 'error',
      currentValue: phone,
      suggestedValue: cleanPhone,
    });
  } else if (!/^9\d{8}$/.test(cleanPhone)) {
    warnings.push({ field: 'phone', message: 'WhatsApp deberia empezar con 9 en Peru', severity: 'warning' });
  }

  const district = row['Distrito']?.toString().trim() || '';
  if (!district) {
    errors.push({ field: 'district', message: 'Distrito es obligatorio', severity: 'error' });
  } else {
    const validDistrictList = validDistrictListFrom(validDistricts);
    const normalizedDistricts = new Set(validDistrictList.map(normalizeDistrict));
    const isValid = validDistrictList.includes(district) || normalizedDistricts.has(normalizeDistrict(district));

    if (!isValid) {
      const normalized = district.toLowerCase().trim();
      const suggestion = DISTRICT_SUGGESTIONS[normalized];

      if (suggestion && suggestion !== district) {
        suggestions.district = suggestion;
        warnings.push({
          field: 'district',
          message: `Distrito "${district}" no reconocido`,
          severity: 'warning',
          suggestion,
          autoFixable: true,
        });
      } else if (!suggestion) {
        errors.push({
          field: 'district',
          message: `Distrito "${district}" no esta en la cobertura configurada`,
          severity: 'error',
          showDistrictPicker: true,
        });
      }
    }
  }

  const address = row['Direccion']?.toString().trim() || '';
  if (!address) {
    errors.push({ field: 'address', message: 'Direccion es obligatoria', severity: 'error' });
  } else if (address.length < 10) {
    warnings.push({ field: 'address', message: 'Direccion parece muy corta', severity: 'warning' });
  }

  const items = getBulkItems(row);
  if (items.length === 0) {
    errors.push({ field: 'product', message: 'Debe proporcionar al menos un SKU', severity: 'error' });
  }

  const charge = row['Cobrar']?.toString().trim() || '0';
  if (charge) {
    const chargeNum = parseFloat(charge);
    if (Number.isNaN(chargeNum)) {
      warnings.push({
        field: 'charge',
        message: 'Monto a cobrar debe ser numerico',
        severity: 'warning',
        currentValue: charge,
        suggestedValue: '0',
      });
    } else if (chargeNum < 0) {
      warnings.push({ field: 'charge', message: 'Monto a cobrar no puede ser negativo', severity: 'warning' });
    }
  }

  items.forEach(item => {
    const quantityNum = parseInt(item.quantity, 10);
    if (Number.isNaN(quantityNum)) {
      warnings.push({
        field: `Cantidad ${item.index}`,
        message: `Cantidad ${item.index} debe ser un numero entero`,
        severity: 'warning',
        currentValue: item.quantity,
        suggestedValue: '1',
      });
    } else if (quantityNum < 1) {
      warnings.push({
        field: `Cantidad ${item.index}`,
        message: `Cantidad ${item.index} debe ser al menos 1`,
        severity: 'warning',
        suggestedValue: '1',
      });
    }
  });

  const notes = row['Notas']?.toString().trim() || '';
  const latitude = parseCoordinate(row['Latitud']);
  const longitude = parseCoordinate(row['Longitud']);

  if (latitude.raw || longitude.raw) {
    if (!latitude.raw || !longitude.raw) {
      errors.push({
        field: 'coordinates',
        message: 'Latitud y Longitud deben completarse juntas',
        severity: 'error',
      });
    }
    if (latitude.invalid) {
      errors.push({ field: 'Latitud', message: 'Latitud debe ser numerica', severity: 'error' });
    } else if (latitude.raw && (latitude.number < -90 || latitude.number > 90)) {
      errors.push({ field: 'Latitud', message: 'Latitud debe estar entre -90 y 90', severity: 'error' });
    }
    if (longitude.invalid) {
      errors.push({ field: 'Longitud', message: 'Longitud debe ser numerica', severity: 'error' });
    } else if (longitude.raw && (longitude.number < -180 || longitude.number > 180)) {
      errors.push({ field: 'Longitud', message: 'Longitud debe estar entre -180 y 180', severity: 'error' });
    }
  }

  const data = {
    'Nombre y Apellido': name,
    WhatsApp: cleanPhone,
    Distrito: suggestions.district || district,
    Direccion: address,
    Latitud: latitude.raw,
    Longitud: longitude.raw,
    'Cobrar': charge || '0',
    Notas: notes,
  };

  for (let index = 1; index <= MAX_BULK_ITEMS; index += 1) {
    const item = items[index - 1];
    data[`SKU ${index}`] = item?.sku || '';
    data[`Cantidad ${index}`] = item?.quantity || (index === 1 ? '1' : '');
  }

  return {
    rowNumber,
    isValid: errors.length === 0,
    hasWarnings: warnings.length > 0,
    canAutoFix: warnings.some(w => w.autoFixable),
    errors,
    warnings,
    suggestions,
    data,
  };
}

export function validateAllDeliveries(rows, validDistricts) {
  const validatedRows = rows.map((row, index) =>
    validateDelivery(row, index + 2, validDistricts)
  );

  const validCount = validatedRows.filter(r => r.isValid).length;
  const errorCount = validatedRows.filter(r => !r.isValid).length;
  const warningCount = validatedRows.filter(r => r.hasWarnings && r.isValid).length;

  return {
    rows: validatedRows,
    summary: {
      total: rows.length,
      valid: validCount,
      errors: errorCount,
      warnings: warningCount,
      canProceed: errorCount === 0,
    },
  };
}

export function autoFixRow(validatedRow) {
  const fixed = { ...validatedRow, data: { ...validatedRow.data } };

  if (fixed.suggestions.district) {
    fixed.data['Distrito'] = fixed.suggestions.district;
  }

  if (fixed.data['WhatsApp']) {
    fixed.data['WhatsApp'] = fixed.data['WhatsApp'].replace(/\D/g, '');
  }

  fixed.warnings.forEach(warning => {
    if (warning.suggestedValue && warning.field in fixed.data) {
      fixed.data[warning.field] = warning.suggestedValue;
    }
  });

  return fixed;
}
